<template>
    <ProfileView
        v-if="user && !isEditing"
        :user="userToShow"
        :can-edit="isOwnProfile"
      :available-stats-tabs="availableStatsTabs"
      :active-stats-tab="activeStatsTab"
      :stats-loading="statsLoading"
        @edit-profile="toggleEdit"
      @change-stats-tab="handleStatsTabChange"
    />
    <ProfileEdit
        v-if="user && isEditing && isOwnProfile"
        @cancel-edit="toggleEdit"
        @update-user="updateUser"
    />
    <loading-modal ref="loadingModal" />
</template>



<script>

import ProfileView from '@/components/profile/ProfileView.vue'
import ProfileEdit from '@/components/profile//ProfileEdit.vue'
import LoadingModal from "@/services/LoadingModal.vue";
import {mapActions, mapState} from "vuex";
import router from "@/router/router";
import echoService from "@/services/echoService";

export default {
  name: 'UserProfile',
  components: {
    LoadingModal,
    ProfileView,
    ProfileEdit
  },
  data() {
    return {
      isEditing: false,
      externalUser: null,
      activeStatsTab: null,
      statsLoading: false,
      loadedStatsTabs: [],
    }
  },
  computed: {
    ...mapState('user', ['user','token']),
    currentUser() {
      return this.user;
    },
    profileId() {
      return this.$route.params.id ? parseInt(this.$route.params.id) : null;
    },
    isOwnProfile() {
      return !this.profileId || this.profileId === this.currentUser.id;
    },
    userToShow() {
      return this.isOwnProfile ? this.currentUser : this.externalUser;
    },
    availableStatsTabs() {
      const roles = Array.isArray(this.userToShow?.roles) ? this.userToShow.roles : [];
      const normalized = roles
        .map(role => String(role?.name || '').trim().toLowerCase())
        .filter(Boolean);

      const tabs = [];

      if (normalized.includes('athlete')) {
        tabs.push('athlete');
      }
      if (normalized.includes('judge')) {
        tabs.push('judge');
      }
      if (normalized.includes('organizer')) {
        tabs.push('organizer');
      }

      return tabs;
    }
  },
  async mounted() {
    await this.loadProfileData();
    if (this.user) {
      this.listenToUserUpdates(this.user.id);
    }
  },
  methods: {
    ...mapActions('user', ['setLogout','setUser']),
    async loadProfileData() {
      const currentId = this.currentUser?.id;
      const targetId = this.profileId || currentId;

      if (!targetId) {
        return;
      }

      if (this.isOwnProfile) {
        await this.fetchExternalUser(targetId, { updateStore: true });
      } else {
        await this.fetchExternalUser(targetId);
      }

      this.ensureActiveStatsTab();
      if (this.activeStatsTab) {
        await this.ensureStatsLoaded(this.activeStatsTab);
      }
    },
    listenToUserUpdates(userId) {
      echoService.channel(`users.${userId}`)
          .listen('.UserUpdated', (e) => {
            const updatedUser = {
              ...this.user,
              ...e.user // solo actualiza los campos nuevos
            };
            this.$store.dispatch('user/setUser', updatedUser)
            // this.setUser(updatedUser);
          })
          .error(error => {
            console.error('❌ Error en WebSocket:', error);
          });
    },
    async fetchExternalUser(id, { updateStore = false } = {}) {
      if (!id || Number.isNaN(Number(id))) return;

      try {
        this.showLoading();
        const profileResponse = await this.$axios.get(`/check_profile/${id}`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        if (profileResponse?.data?.user) {
          const mergedUser = {
            ...profileResponse.data.user,
            stats: {},
          };
          if (updateStore) {
            this.setUser(mergedUser);
          } else {
            this.externalUser = mergedUser;
          }
          this.loadedStatsTabs = [];
        } else if (!updateStore) {
          this.$router.push('/not-found'); // o donde quieras redirigir
        }
      } catch (err) {
        console.error("Error al obtener perfil externo:", err);
        if (!updateStore) {
          this.$router.push('/not-found');
        }
      } finally {
        this.hideLoading();
      }
    },
    async fetchAthleteStats(id) {
      try {
        const response = await this.$axios.get(`/athletes/${id}/stats`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        return response?.data?.stats || null;
      } catch (error) {
        console.error('Error al obtener estadísticas de atleta:', error);
        return null;
      }
    },
    async fetchJudgeStats(id) {
      try {
        const response = await this.$axios.get(`/judges/${id}/stats`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        return response?.data?.stats || null;
      } catch (error) {
        console.error('Error al obtener estadísticas de juez:', error);
        return null;
      }
    },
    async fetchOrganizerStats(id) {
      try {
        const response = await this.$axios.get(`/organizers/${id}/stats`, {
          headers: { Authorization: `Bearer ${this.token}` },
        });

        return response?.data?.stats || null;
      } catch (error) {
        console.error('Error al obtener estadísticas de organizador:', error);
        return null;
      }
    },
    mergeRoleStats(user, roleKey, roleStats) {
      if (!user || !roleKey || !roleStats) {
        return user;
      }

      const existingStats = user.stats && typeof user.stats === 'object'
        ? user.stats
        : {};
      const existingRole = existingStats[roleKey] && typeof existingStats[roleKey] === 'object'
        ? existingStats[roleKey]
        : {};

      return {
        ...user,
        stats: {
          ...existingStats,
          [roleKey]: {
            ...existingRole,
            ...roleStats,
          },
        },
      };
    },
    ensureActiveStatsTab() {
      if (!this.availableStatsTabs.length) {
        this.activeStatsTab = null;
        return;
      }

      if (this.availableStatsTabs.length > 1 && this.availableStatsTabs.includes('athlete')) {
        this.activeStatsTab = 'athlete';
        return;
      }

      if (!this.activeStatsTab || !this.availableStatsTabs.includes(this.activeStatsTab)) {
        [this.activeStatsTab] = this.availableStatsTabs;
      }
    },
    async handleStatsTabChange(tab) {
      if (!tab || tab === this.activeStatsTab) {
        return;
      }

      this.activeStatsTab = tab;
      await this.ensureStatsLoaded(tab);
    },
    async ensureStatsLoaded(tab) {
      const targetId = this.userToShow?.id || this.profileId || this.currentUser?.id;

      if (!tab || !targetId || this.loadedStatsTabs.includes(tab)) {
        return;
      }

      this.statsLoading = true;

      try {
        let stats = null;

        if (tab === 'athlete') {
          stats = await this.fetchAthleteStats(targetId);
        }
        if (tab === 'judge') {
          stats = await this.fetchJudgeStats(targetId);
        }
        if (tab === 'organizer') {
          stats = await this.fetchOrganizerStats(targetId);
        }

        if (!stats) {
          return;
        }

        const mergedUser = this.mergeRoleStats(this.userToShow, tab, stats);
        if (this.isOwnProfile) {
          this.setUser(mergedUser);
        } else {
          this.externalUser = mergedUser;
        }

        this.loadedStatsTabs = [...this.loadedStatsTabs, tab];
      } finally {
        this.statsLoading = false;
      }
    },
    toggleEdit() {
      this.isEditing = !this.isEditing
    },
    async updateUser(updatedUser) {
      try {
        this.showLoading();
        const { role_settings = [], ...profilePayload } = updatedUser;

        const response = await this.$axios.post("/update_profile", {
          updatedUser: profilePayload
        }, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        const discoveryResponse = await this.$axios.patch('/me/discovery-settings', {
          role_settings,
        }, {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response.status === 200) {
          this.setUser({
            ...response.data.user,
            ...(discoveryResponse?.data?.user || {}),
          });
          await router.push("/profile");
          this.hideLoading();
        }
      } catch (error) {
        console.error('Error al guardar usuario:', error);
      }

    },

    showLoading() {
      this.$nextTick(() => {
        this.$refs.loadingModal.showLoading();
      });
    },
    hideLoading() {
      this.$nextTick(() => {
        this.$refs.loadingModal.hideLoading();
      });
    },
  }
}
</script>

<style scoped>
/* Puedes incluir estilos globales aquí si es necesario */
</style>
